import { db, auth } from '../firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { format, addDays, startOfWeek } from 'date-fns';

// Check if token is expired
function isTokenExpired() {
  const expiry = localStorage.getItem('googleTokenExpiry');
  if (!expiry) return true;
  return Date.now() > parseInt(expiry, 10);
}

// Clear all Google Calendar tokens
function clearGoogleTokens() {
  localStorage.removeItem('googleAccessToken');
  localStorage.removeItem('googleTokenExpiry');
}

export async function signInWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/calendar.readonly');
    provider.setCustomParameters({
      prompt: 'consent' // Force consent to get fresh token
    });

    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential.accessToken;

    if (!accessToken) {
      throw new Error('Failed to get access token from Google');
    }

    // Store access token and expiry (Google tokens typically expire in 1 hour)
    const expiresInMs = 3600 * 1000; // 1 hour
    localStorage.setItem('googleAccessToken', accessToken);
    localStorage.setItem('googleTokenExpiry', String(Date.now() + expiresInMs));

    return { success: true, accessToken };
  } catch (error) {
    console.error('Error signing in with Google:', error);
    return { success: false, error };
  }
}

export async function fetchGoogleCalendarEvents() {
  try {
    const accessToken = localStorage.getItem('googleAccessToken');

    // Check if token exists
    if (!accessToken) {
      return { success: false, error: new Error('No access token found'), needReauth: true };
    }

    // Check if token is expired
    if (isTokenExpired()) {
      clearGoogleTokens();
      return { success: false, error: new Error('Token expired'), needReauth: true };
    }

    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: new Error('No authenticated user'), needReauth: false };
    }

    // Get the user's timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Calculate date range (±35 days from current week start for wider coverage)
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 0 });
    const startDate = addDays(weekStart, -35);
    const endDate = addDays(weekStart, 35);

    const timeMin = startDate.toISOString();
    const timeMax = endDate.toISOString();

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&timeZone=${encodeURIComponent(userTimeZone)}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    // Handle API errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const status = response.status;

      // Token-related errors that require re-authentication
      if (status === 401 || status === 403) {
        clearGoogleTokens();
        return {
          success: false,
          error: new Error(`Google Calendar API error: ${status}`),
          needReauth: true
        };
      }

      throw new Error(`Google Calendar API error: ${status} - ${JSON.stringify(errorData)}`);
    }

    const calendarData = await response.json();
    const events = calendarData.items || [];

    // Store Google events in Firestore
    const googleEventsRef = collection(db, 'lawyers', user.uid, 'google_events');

    // Get existing events to handle deletions
    const existingEventsSnapshot = await getDocs(googleEventsRef);
    const existingEventIds = new Set(existingEventsSnapshot.docs.map(doc => doc.id));

    const googleEventIds = new Set(events.map(event => event.id));

    // Delete events that no longer exist in Google Calendar
    for (const docId of existingEventIds) {
      if (!googleEventIds.has(docId)) {
        await deleteDoc(doc(googleEventsRef, docId));
      }
    }

    // Store new events and update existing ones
    let eventCount = 0;
    for (const event of events) {
      if (event.start?.dateTime) {
        const eventDate = new Date(event.start.dateTime);
        const eventData = {
          id: event.id,
          title: event.summary || 'Unnamed Event',
          summary: event.summary || 'Unnamed Event', // WeeklyCalendar uses 'summary'
          description: event.description || '',
          date: format(eventDate, 'yyyy-MM-dd'), // Store as yyyy-MM-dd for consistency
          time: format(eventDate, 'HH:mm'),
          start: {
            dateTime: event.start.dateTime,
            timeZone: event.start.timeZone || userTimeZone,
          },
          end: {
            dateTime: event.end?.dateTime,
            timeZone: event.end?.timeZone || userTimeZone,
          },
          isGoogleEvent: true,
          syncedAt: new Date().toISOString(),
        };

        await setDoc(doc(googleEventsRef, event.id), eventData);
        eventCount++;
      }
    }

    // Save last sync time and event count
    localStorage.setItem('lastSyncTime', new Date().toISOString());
    localStorage.setItem('googleEventCount', eventCount.toString());

    return { success: true, eventCount };
  } catch (error) {
    console.error('Error fetching Google Calendar events:', error);

    // Check for token-related errors in error message
    const errorMsg = error.message || '';
    if (
      errorMsg.includes('invalid_token') ||
      errorMsg.includes('unauthorized') ||
      errorMsg.includes('invalid_grant') ||
      errorMsg.includes('access_denied')
    ) {
      clearGoogleTokens();
      return { success: false, error, needReauth: true };
    }

    return { success: false, error };
  }
}

export async function syncGoogleEvents(forceSync = false) {
  // If token is expired or missing, always try to sync (which will trigger re-auth)
  const accessToken = localStorage.getItem('googleAccessToken');
  if (!accessToken || isTokenExpired()) {
    return await fetchGoogleCalendarEvents();
  }

  // Check if sync is needed based on time
  const lastSyncTime = localStorage.getItem('lastSyncTime');
  const now = new Date();

  let needsSync = forceSync;

  if (!needsSync && lastSyncTime) {
    const lastSync = new Date(lastSyncTime);
    // Only sync if more than 5 minutes have passed since last sync
    needsSync = now.getTime() - lastSync.getTime() > 5 * 60 * 1000;
  } else if (!lastSyncTime) {
    needsSync = true;
  }

  if (needsSync) {
    return await fetchGoogleCalendarEvents();
  }

  return { success: true, message: 'Recent sync exists, skipped', eventCount: parseInt(localStorage.getItem('googleEventCount') || '0', 10) };
}